import React, { useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { db } from '../config/firebase';
import { collection, addDoc, Timestamp } from 'firebase/firestore';
import './Donate.css';

const unitLogo = '/images/LP_Unit_logo.png';

const tiers = [
  {
    name: 'JSOC',
    price: 100,
    perks: [
      'All perks from Tier 1, 2 and 3',
      'Early access to purchase newly released gear or uniform options',
      'MILSIM themed real-world item sent to you every quarter. (If shipping outside of the United States, requestor will pay shipping cost.)'
    ]
  },
  {
    name: 'Tier 1',
    price: 75,
    perks: [
      'All perks from Tier 2 and 3',
      '1x Helmet and Vest Revision a quarter'
    ]
  },
  {
    name: 'Tier 2',
    price: 50,
    perks: [
      'All perks from Tier 3',
      '1x New Zapped uniform release per quarter.'
    ]
  },
  {
    name: 'Tier 3',
    price: 25,
    perks: [
      '1x Custom Locker (This covers changes per quarter if already established)'
    ]
  }
];

function Donate() {
  const { user, userData, isAdmin, logout } = useAuth();
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const [showDonationForm, setShowDonationForm] = useState(false);
  const [selectedTier, setSelectedTier] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  
  // Form fields
  const [discordName, setDiscordName] = useState('');
  const [paypalEmail, setPaypalEmail] = useState('');
  const [amountSent, setAmountSent] = useState('');

  const handleDonateClick = (tier) => {
    setSelectedTier(tier);
    setShowDisclaimer(true);
  };

  const handleAcknowledge = () => {
    setShowDisclaimer(false);
    setShowDonationForm(true);
  };

  const handleDoNotAcknowledge = () => {
    setShowDisclaimer(false);
    setSelectedTier(null);
  };

  const handleSubmitDonation = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await addDoc(collection(db, 'donations'), {
        discordName,
        tier: selectedTier.name,
        tierPrice: selectedTier.price,
        paypalEmail,
        amountSent: parseFloat(amountSent),
        userId: user.uid,
        userEmail: userData?.email || '',
        username: userData?.username || '',
        status: 'Pending',
        createdAt: Timestamp.now()
      });

      setSubmitSuccess(true);
      setShowDonationForm(false);
    } catch (error) {
      console.error('Error submitting donation:', error);
      alert('Error submitting donation. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitSuccess) {
    return (
      <div className="donate-page" style={{ backgroundImage: `url(/images/CJN6.png)`, backgroundSize: 'cover', backgroundPosition: 'center', backgroundAttachment: 'fixed' }}>
        <nav className="donate-navbar">
          <Link to="/" className="nav-left">
            <img src={unitLogo} alt="Logo" className="unit-logo" />
            <h1 className="site-title">Creature Supply</h1>
          </Link>
          <div className="nav-right">
            <Link to="/" className="nav-link">Home</Link>
          </div>
        </nav>
        <div className="success-container">
          <div className="success-card">
            <div className="success-icon">✓</div>
            <h2>Donation Submitted!</h2>
            <p>Your {selectedTier?.name} donation has been recorded.</p>
            <p className="success-details">Allow 24-48 hours for processing.</p>
            <Link to="/" className="btn-home">Return Home</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="donate-page" style={{ backgroundImage: `url(/images/CJN6.png)`, backgroundSize: 'cover', backgroundPosition: 'center', backgroundAttachment: 'fixed' }}>
      {/* Navigation */}
      <nav className="donate-navbar">
        <Link to="/" className="nav-left">
          <img src={unitLogo} alt="Logo" className="unit-logo" />
          <h1 className="site-title">Creature Supply</h1>
        </Link>
        <div className="nav-right">
          <Link to="/" className="nav-link">Home</Link>
          {user && (
            <>
              <span className="user-name">{userData?.serverNickname || userData?.username || userData?.email || ''}</span>
              <button className="logout-btn" onClick={logout}>Logout</button>
            </>
          )}
        </div>
      </nav>

      {/* Main Content */}
      <div className="donate-content">
        <p className="donate-header">All Prices are quarterly (every three months) and have enrollment periods that will be announced in the unit discord.</p>
        
        <div className="tiers-container">
          {tiers.map((tier, index) => (
            <div key={tier.name} className="tier-card">
              <h2 className="tier-name">{tier.name} - ${tier.price}</h2>
              <ul className="tier-perks">
                {tier.perks.map((perk, i) => (
                  <li key={i}>{perk}</li>
                ))}
              </ul>
              <button 
                className="btn-donate"
                onClick={() => handleDonateClick(tier)}
              >
                Click to Donate
              </button>
            </div>
          ))}
        </div>

        <p className="disclaimer-footer">Disclaimer: All prices are in USD.</p>
      </div>

      {/* Disclaimer Modal */}
      {showDisclaimer && (
        <div className="modal-overlay" onClick={() => setShowDisclaimer(false)}>
          <div className="modal-content disclaimer-modal" onClick={e => e.stopPropagation()}>
            <h2 className="modal-title disclaimer-title">DONATION DISCLAIMER</h2>
            <div className="disclaimer-text">
              <p className="disclaimer-item">
                1.) Donations are 100% voluntary and are not a requirement for initial or continued membership in CSQN; if you put yourself in poverty donating, we will laugh at you as you eat your ramen noodles. Only badged members are permitted to donate.
              </p>
              <p className="disclaimer-item">
                2.) Donation does not grant special privileges, ownership, input, or control over decisions made within the unit.
              </p>
              <p className="disclaimer-item">
                3.) Donations do not grant immunity from removal from the unit in cases of release for standards, non-attendance, or breaches of the code of conduct.
              </p>
              <p className="disclaimer-item">
                4.) Donations are non-refundable (excluding overpayment) and are not paying for a service, do not create obligations, or permit exclusive rights.
              </p>
              <p className="disclaimer-item">
                5.) If you donate outside of the enrollment period for that quarter benefits will not be redeemable.
              </p>
              <p className="disclaimer-item disclaimer-highlight">
                6.) Donating signifies you have read and understand the above terms and conditions, and waive any claim to entitlement beyond what is outlined in your paid tier.
              </p>
            </div>
            <div className="disclaimer-actions">
              <button className="btn-acknowledge" onClick={handleAcknowledge}>
                I Acknowledge
              </button>
              <button className="btn-do-not-acknowledge" onClick={handleDoNotAcknowledge}>
                I Do Not Acknowledge
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Donation Form Modal */}
      {showDonationForm && (
        <div className="modal-overlay" onClick={() => setShowDonationForm(false)}>
          <div className="modal-content donation-form-modal" onClick={e => e.stopPropagation()}>
            <h2 className="modal-title">Complete Your Donation</h2>
            <form onSubmit={handleSubmitDonation}>
              <div className="form-group">
                <label>Q1: What is your CSQN Discord name?</label>
                <input 
                  type="text" 
                  value={discordName}
                  onChange={(e) => setDiscordName(e.target.value)}
                  required
                  placeholder="J. Smith"
                />
              </div>
              <div className="form-group">
                <label>Q2: What Tier did you select?</label>
                <select 
                  value={selectedTier?.name || ''}
                  onChange={(e) => {
                    const tier = tiers.find(t => t.name === e.target.value);
                    setSelectedTier(tier);
                  }}
                  required
                >
                  {tiers.map(tier => (
                    <option key={tier.name} value={tier.name}>
                      {tier.name} - ${tier.price}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Q3: What is your email associated with your PayPal?</label>
                <input 
                  type="email" 
                  value={paypalEmail}
                  onChange={(e) => setPaypalEmail(e.target.value)}
                  required
                  placeholder="your@email.com"
                />
              </div>
              <div className="form-group">
                <label>Q4: How much did you send?</label>
                <input 
                  type="number" 
                  value={amountSent}
                  onChange={(e) => setAmountSent(e.target.value)}
                  required
                  placeholder="50"
                  min="1"
                />
              </div>

              <div className="form-instructions">
                <p>Ensure you send payment before clicking submit.</p>
                <p>Allow 24-48 hours for processing. If you have any issues, please send a message to C. Rabbit or E. Malick.</p>
                <p>All payments should be sent as Friends and Family with the comment "bill pool".</p>
                <p className="payment-address">Please send payments to: <span className="placeholder">https://www.paypal.com/paypalme/CreatureSupply</span></p>
              </div>

              <button 
                type="submit" 
                className="btn-submit"
                disabled={submitting}
              >
                {submitting ? 'Submitting...' : 'Submit'}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Donate;
